import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Paper,
  Chip,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getRestaurant, getRestaurants, getRestaurantInfo, getMenuItems, getMenuItemAddons, createOrder, API_ORIGIN } from '../services/api';
import MenuItemModal from '../components/MenuItemModal';
import MenuItemCard from '../components/Menu/MenuItemCard';
import SectionHeader from '../components/Menu/SectionHeader';
import MenuSearchBar from '../components/Menu/MenuSearchBar';
import ServiceRequestButtons from '../components/Menu/ServiceRequestButtons';
import MenuLoadingSkeleton from '../components/Menu/MenuLoadingSkeleton';
import MenuHero from '../components/Menu/MenuHero';
import CartModal from '../components/Menu/CartModal';
import OtpCheckoutDialog from '../components/Menu/OtpCheckoutDialog';
import EmptyState from '../components/ui/EmptyState';
import PriceTag from '../components/ui/PriceTag';
import { formatPrice } from '../utils/formatPrice';
import { calculateOrderTotals } from '../utils/taxCalc';
import { ThemeProvider, useTheme } from '../context/ThemeContext';
import '../styles/menu-tokens.css';
import './Menu.css';

/* Source of truth for the sticky nav offset. Mirrored in CSS as
   --menu-sticky-top (menu-tokens.css) and applied to .menu-page below. JS
   keeps the number for getBoundingClientRect math; CSS uses the var so
   scrollMarginTop / sticky position stay in sync without two magic 56s. */
const STICKY_NAV_TOP_OFFSET = 56;
const SEARCH_DEBOUNCE_MS = 300;
const MENU_CACHE_KEY = (id, lang) => `menu_${id}_${lang}`;

function sectionToKey(name) {
  return (name || '')
    .trim()
    .split(/\s+/)
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join('');
}

const MenuContent = () => {
  const { t, i18n } = useTranslation();
  const { restaurantId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme, loading: themeLoading } = useTheme();
  const isMobileViewport = useMediaQuery('(max-width: 767px)');
  /* Default to "1" (the identifier), not "Table 1" — every consumer (hero,
     KDS, OrderCard, ServiceRequestsPanel, ManagerDashboard) prefixes the
     localized "Table " label itself. Using "Table 1" here caused the hero
     to render "Table Table 1" when no ?table= param was present. */
  const tableNumber = searchParams.get('table') || '1';
  const demoRequested = searchParams.get('mode') === 'demo';
  const orderType = searchParams.get('type') === 'car' ? 'car' : 'dine_in';
  const isCarOrder = orderType === 'car';

  const [restaurant, setRestaurant] = useState(null);
  // Demo mode (banner, guided flow, is_demo orders) is only honored for restaurants
  // explicitly flagged is_demo — otherwise ?mode=demo on a real tenant's URL would
  // expose the demo experience. `restaurant` loads async, so this is false until it
  // arrives, then flips on for a flagged restaurant.
  const isDemo = demoRequested && Boolean(restaurant?.is_demo);
  const tableDisplay = isDemo
    ? 'Demo'
    : isCarOrder
      ? t('menu.carOrderMode')
      : tableNumber;
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [currency, setCurrency] = useState(null);
  // Translated category (section) names for the active language: { baseName: translated }.
  const [sectionTranslations, setSectionTranslations] = useState({});
  /* Tracks the most recent fetch failure. Distinguishes "menu loaded but
     happens to be empty" (legitimate state for a freshly-created restaurant)
     from "menu fetch errored" (user can retry). */
  const [loadError, setLoadError] = useState(false);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [addonsForModal, setAddonsForModal] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [demoOrderPlaced, setDemoOrderPlaced] = useState(false);
  const [demoOrderId, setDemoOrderId] = useState(null);
  const [orderResultDialogOpen, setOrderResultDialogOpen] = useState(false);
  const [orderResultDialogTitle, setOrderResultDialogTitle] = useState('');
  const [orderResultDialogMessage, setOrderResultDialogMessage] = useState('');
  const [placingOrder, setPlacingOrder] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [carNumber, setCarNumber] = useState('');
  const [carColor, setCarColor] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [activeSection, setActiveSection] = useState(null);
  const [headerShrunk, setHeaderShrunk] = useState(false);
  /* Initial banner state honors a persisted preference if the diner has
     collapsed it before; otherwise defaults to collapsed on small screens
     (<640px) so the menu isn't pushed half a fold below the top, and
     expanded on tablet+ where the full tour fits comfortably. SSR-safe
     fallback returns false (no banner) — the effect below corrects after
     mount when window is available. */
  const [bannerCollapsed, setBannerCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const stored = window.localStorage.getItem('menuDemoBannerCollapsed');
      if (stored === 'true') return true;
      if (stored === 'false') return false;
    } catch (_) { /* localStorage may be blocked */ }
    return window.innerWidth < 640;
  });
  /* Persist only on user-driven toggles (button taps), NOT on scroll-driven
     auto-collapse — otherwise a single scroll would lock the banner closed
     for that diner forever. */
  const setBannerCollapsedPersisted = useCallback((collapsed) => {
    setBannerCollapsed(collapsed);
    try { window.localStorage.setItem('menuDemoBannerCollapsed', String(collapsed)); } catch (_) {}
  }, []);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [otherDemoRestaurants, setOtherDemoRestaurants] = useState([]);
  const sectionRefs = useRef({});
  const sectionsContainerRef = useRef(null);
  const categoriesRef = useRef(null);
  const isRTL = i18n.language === 'ar';

  const currentLang = (i18n.language && ['en', 'es', 'ar'].includes(i18n.language)) ? i18n.language : 'en';

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const cacheKey = MENU_CACHE_KEY(restaurantId, currentLang);
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const { items, currency: cachedCurrency, sectionTranslations: cachedSections, ts } = JSON.parse(cached);
          if (items && Date.now() - ts < 5 * 60 * 1000) {
            setMenuItems(Array.isArray(items) ? items : []);
            setCurrency(cachedCurrency ?? null);
            setSectionTranslations(cachedSections || {});
          }
        }
      } catch {}
      const [restaurantRes, infoRes, menuRes] = await Promise.all([
        getRestaurant(restaurantId),
        getRestaurantInfo(restaurantId, currentLang).catch(() => ({ data: null })),
        getMenuItems(restaurantId, currentLang)
      ]);
      // On first scan (diner hasn't manually picked a language) open the menu in the
      // restaurant manager's configured default. 'auto'/missing falls through to the
      // browser-detected language. Once applied, i18n.changeLanguage re-runs this effect
      // via the currentLang dependency; the re-run sees currentLang === def so it can't
      // loop, and the detector caches def so later visits don't switch again.
      const def = infoRes?.data?.default_language;
      const langChosen = localStorage.getItem('langChosen') === '1';
      if (!langChosen && ['en', 'es', 'ar'].includes(def) && def !== currentLang) {
        i18n.changeLanguage(def);
        return;
      }
      setRestaurant(restaurantRes.data);
      setRestaurantInfo(infoRes?.data || null);
      const menuData = menuRes.data;
      const items = Array.isArray(menuData?.items) ? menuData.items : [];
      const sectionTrans = menuData?.sectionTranslations || {};
      setMenuItems(items);
      setCurrency(menuData?.currency ?? restaurantRes.data?.currency ?? null);
      setSectionTranslations(sectionTrans);
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          items,
          currency: menuData?.currency ?? restaurantRes.data?.currency ?? null,
          sectionTranslations: sectionTrans,
          ts: Date.now()
        }));
      } catch {}
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, currentLang, i18n]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHeaderShrunk(y > 80);
      if (isDemo) setBannerCollapsed(y > 240);
      if (y > 200) setHasScrolled(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isDemo]);

  useEffect(() => {
    if (!isDemo) return;
    let cancelled = false;
    getRestaurants()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const currentId = String(restaurantId);
        setOtherDemoRestaurants(
          list.filter((r) => r.is_demo && String(r.id) !== currentId).slice(0, 4)
        );
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isDemo, restaurantId]);

  const addToCart = useCallback((itemOrPayload) => {
    const item = itemOrPayload.quantity != null
      ? itemOrPayload
      : { ...itemOrPayload, quantity: 1, price: parseFloat(itemOrPayload.price), selectedAddons: [] };
    const addonsKey = (item.selectedAddons && item.selectedAddons.length)
      ? JSON.stringify(item.selectedAddons.map(a => ({ c: a.category, o: a.option, p: a.price })))
      : '';
    setCart(prev => {
      const existingIndex = prev.findIndex(i =>
        i.id === item.id &&
        (i.selectedAddons && i.selectedAddons.length ? JSON.stringify(i.selectedAddons.map(a => ({ c: a.category, o: a.option, p: a.price }))) : '') === addonsKey
      );
      if (existingIndex >= 0) {
        return prev.map((i, idx) =>
          idx === existingIndex ? { ...i, quantity: i.quantity + (item.quantity || 1) } : i
        );
      }
      return [...prev, {
        id: item.id,
        name: item.name,
        price: parseFloat(item.price),
        quantity: item.quantity || 1,
        selectedAddons: item.selectedAddons || []
      }];
    });
  }, []);

  const openMenuItemModal = async (item) => {
    setSelectedItem(item);
    setShowModal(true);
    if (Array.isArray(item.addons)) {
      setAddonsForModal(item.addons);
      setAddonsLoading(false);
      return;
    }
    setAddonsForModal([]);
    setAddonsLoading(true);
    try {
      const res = await getMenuItemAddons(item.id);
      setAddonsForModal(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching addons:', err);
      setAddonsForModal([]);
    } finally {
      setAddonsLoading(false);
    }
  };

  const closeMenuItemModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setAddonsForModal([]);
  };

  const updateQuantity = useCallback((index, delta) => {
    setCart(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const newQuantity = item.quantity + delta;
      return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
    }).filter(Boolean));
  }, []);

  /* Explicit single-tap remove. Decreases-to-zero still works via updateQuantity
     for accidental over-decrements; this is the deliberate-remove path. The
     CartModal renders an inline "Removed · Undo" affordance for 4s before this
     handler fires.

     Takes the item REFERENCE captured at trash time, not an index. With
     index-based removal, two pending undos firing in sequence used a stale
     index for the second call: the first removal shifted later items
     down, so the second filter targeted the wrong slot and one item
     survived. Reference-identity filtering is shift-proof. */
  const removeItem = useCallback((target) => {
    setCart(prev => prev.filter(i => i !== target));
  }, []);

  const getCartQuantityForItem = useCallback((item) => {
    if (item.hasAddons || item.hasSizes) return 0;
    return cart
      .filter(i => i.id === item.id && !(i.selectedAddons && i.selectedAddons.length))
      .reduce((sum, i) => sum + i.quantity, 0);
  }, [cart]);

  const handleDecreaseForItem = useCallback((item) => {
    setCart(prev => {
      const idx = prev.findIndex(i => i.id === item.id && !(i.selectedAddons && i.selectedAddons.length));
      if (idx < 0) return prev;
      return prev.map((ci, i) => {
        if (i !== idx) return ci;
        const newQty = ci.quantity - 1;
        return newQty > 0 ? { ...ci, quantity: newQty } : null;
      }).filter(Boolean);
    });
  }, []);

  const totalItems = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);
  const taxBreakdown = useMemo(
    () => calculateOrderTotals(cart, restaurant || {}),
    [cart, restaurant]
  );
  const totalPrice = taxBreakdown.total;
  const getItemSubtotal = useCallback((item) => (parseFloat(item.price) || 0) * (item.quantity || 0), []);

  const buildOrderData = () => {
    const items = cart.map(item => ({
      id: item.id,
      quantity: item.quantity,
      price: parseFloat(item.price),
      selectedAddons: item.selectedAddons && item.selectedAddons.length ? item.selectedAddons : undefined
    }));
    return isCarOrder && !isDemo
      ? {
          order_type: 'car',
          car_number: carNumber.trim(),
          ...(carColor.trim() ? { car_color: carColor.trim() } : {}),
          is_demo: false,
          items
        }
      : {
          table_number: isDemo ? 'Demo' : tableNumber,
          is_demo: isDemo,
          items
        };
  };

  const submitOrder = async ({ checkoutToken } = {}) => {
    setPlacingOrder(true);
    try {
      const orderRes = await createOrder(restaurantId, buildOrderData(), { checkoutToken });
      if (isDemo) {
        setDemoOrderPlaced(true);
        setDemoOrderId(orderRes?.data?.id ?? null);
        setCart([]);
        setShowCart(false);
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        setOrderResultDialogTitle(t('menu.orderDialog.placed'));
        setOrderResultDialogMessage(
          isCarOrder
            ? t('menu.orderDialog.carPlacedMessage', { items: cart.length, total: formatPrice(totalPrice, currency) })
            : t('menu.orderDialog.placedMessage', { table: tableNumber, items: cart.length, total: formatPrice(totalPrice, currency) })
        );
        setOrderResultDialogOpen(true);
        setCart([]);
        setShowCart(false);
        if (isCarOrder) {
          setCarNumber('');
          setCarColor('');
        }
      }
      return true;
    } catch (error) {
      // Race: the manager toggled require_customer_otp ON between menu load and
      // submit. Open the OTP dialog without losing the cart instead of showing
      // a generic error. The backend signals this with { requiresOtp: true }.
      if (error?.response?.status === 401 && error.response.data?.requiresOtp) {
        setOtpDialogOpen(true);
        return false;
      }
      console.error('Error placing order:', error);
      setOrderResultDialogTitle(t('menu.orderDialog.failed'));
      setOrderResultDialogMessage(error.response?.data?.error || t('api.errors.createOrder'));
      setOrderResultDialogOpen(true);
      return false;
    } finally {
      setPlacingOrder(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;
    if (isCarOrder && !isDemo) {
      const trimmedNum = carNumber.trim();
      if (!trimmedNum) {
        setOrderResultDialogTitle(t('menu.orderDialog.failed'));
        setOrderResultDialogMessage(t('menu.cart.carNumberRequired'));
        setOrderResultDialogOpen(true);
        return;
      }
    }
    // Demo orders bypass OTP — they're never delivered to a real kitchen.
    if (!isDemo && restaurant?.require_customer_otp) {
      setOtpDialogOpen(true);
      return;
    }
    await submitOrder();
  };

  const handleOtpVerified = async (checkoutToken) => {
    const ok = await submitOrder({ checkoutToken });
    if (ok) setOtpDialogOpen(false);
  };

  const getImageUrl = useCallback((imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_ORIGIN}${imageUrl}`;
  }, []);

  const heroPhotoPath = useMemo(() => {
    const photos = restaurantInfo?.photos;
    if (!Array.isArray(photos) || photos.length === 0) return null;
    return typeof photos[0] === 'string' ? photos[0] : null;
  }, [restaurantInfo?.photos]);

  const heroStyle = restaurantInfo?.hero_style || 'gradient';
  const heroBgImage = useMemo(() => {
    if (heroStyle === 'image' && restaurantInfo?.hero_image_url) return getImageUrl(restaurantInfo.hero_image_url);
    if (heroStyle === 'image' && heroPhotoPath) return getImageUrl(heroPhotoPath);
    return null;
  }, [heroStyle, restaurantInfo?.hero_image_url, heroPhotoPath, getImageUrl]);

  const logoUrl = useMemo(() => {
    if (restaurantInfo?.logo_url) return getImageUrl(restaurantInfo.logo_url);
    if (heroPhotoPath) return getImageUrl(heroPhotoPath);
    return null;
  }, [restaurantInfo?.logo_url, heroPhotoPath, getImageUrl]);

  const tagline = restaurantInfo?.tagline || '';
  const phone = restaurantInfo?.phone || '';

  const groupMenuItems = useCallback((items) => {
    const grouped = {};
    (Array.isArray(items) ? items : []).forEach(item => {
      const section = item.section || 'Other';
      const category = item.category || null;
      if (!grouped[section]) grouped[section] = {};
      const categoryKey = category || '_no_category';
      if (!grouped[section][categoryKey]) {
        grouped[section][categoryKey] = { categoryName: category, items: [] };
      }
      grouped[section][categoryKey].items.push(item);
    });
    return grouped;
  }, []);

  const getSectionOrder = useCallback((grouped) => {
    const apiOrder = Array.isArray(restaurant?.section_order) ? restaurant.section_order : [];
    let order = apiOrder.filter(s => grouped[s]);
    const all = Object.keys(grouped);
    const missing = all.filter(s => !order.includes(s));
    order = [...order, ...missing.sort()];
    return order.length > 0 ? order : Object.keys(grouped).sort();
  }, [restaurant?.section_order]);

  const filteredItems = useMemo(() => {
    if (!searchDebounced) return menuItems;
    const q = searchDebounced.toLowerCase();
    return menuItems.filter(item =>
      (item.name && item.name.toLowerCase().includes(q)) ||
      (item.description && item.description.toLowerCase().includes(q))
    );
  }, [menuItems, searchDebounced]);

  const itemsToShow = searchDebounced ? filteredItems : menuItems;
  const grouped = useMemo(() => groupMenuItems(itemsToShow), [itemsToShow, groupMenuItems]);
  const sectionOrder = useMemo(() => getSectionOrder(grouped), [grouped, getSectionOrder]);
  const sectionOrderDisplay = useMemo(() => (isRTL ? [...sectionOrder].reverse() : sectionOrder), [sectionOrder, isRTL]);
  const isSearchActive = searchDebounced.length > 0;
  const hasSearchResults = sectionOrder.length > 0;

  const searchSuggestions = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return menuItems
      .filter(item => (item.name && item.name.toLowerCase().includes(q)) || (item.description && item.description.toLowerCase().includes(q)))
      .slice(0, 8);
  }, [menuItems, searchQuery]);

  const popularItems = useMemo(() => menuItems.slice(0, 5), [menuItems]);

  const featuredDemoItems = useMemo(() => {
    if (!isDemo || !Array.isArray(menuItems) || menuItems.length === 0) return [];
    const seenSections = new Set();
    const picks = [];
    const sorted = [...menuItems].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    for (const item of sorted) {
      if (item.is_available === false) continue;
      const section = item.section || '_other';
      if (seenSections.has(section)) continue;
      seenSections.add(section);
      picks.push(item);
      if (picks.length >= 3) break;
    }
    if (picks.length < 3) {
      for (const item of sorted) {
        if (picks.length >= 3) break;
        if (!picks.find((p) => p.id === item.id)) picks.push(item);
      }
    }
    return picks;
  }, [isDemo, menuItems]);

  const demoStepStates = useMemo(() => ({
    browsed: hasScrolled || cart.length > 0 || demoOrderPlaced,
    addedToCart: cart.length > 0 || demoOrderPlaced,
    ordered: demoOrderPlaced,
    dashboard: false,
  }), [hasScrolled, cart.length, demoOrderPlaced]);

  const scrollToMenu = useCallback(() => {
    if (sectionsContainerRef.current) {
      const y = sectionsContainerRef.current.getBoundingClientRect().top + window.scrollY - STICKY_NAV_TOP_OFFSET - 12;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  const handleDemoStepClick = useCallback((step) => {
    if (step === 1) {
      scrollToMenu();
    } else if (step === 2) {
      if (cart.length === 0) {
        scrollToMenu();
      } else {
        setShowCart(true);
      }
    } else if (step === 3) {
      setShowCart(true);
    } else if (step === 4) {
      if (!demoOrderPlaced) return;
      navigate(`/menu/${restaurantId}/demo-dashboard`, {
        state: demoOrderId ? { orderId: demoOrderId } : undefined,
      });
    }
  }, [scrollToMenu, cart.length, demoOrderPlaced, demoOrderId, navigate, restaurantId]);

  useEffect(() => {
    if (!isSearchActive && sectionOrder.length > 0 && activeSection == null) setActiveSection(sectionOrder[0]);
  }, [sectionOrder, isSearchActive, activeSection]);

  useEffect(() => {
    if (isSearchActive || sectionOrder.length === 0) return;
    const refs = sectionRefs.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = entry.target.getAttribute('data-section-id');
          if (id) setActiveSection(id);
        });
      },
      { root: null, rootMargin: `-${STICKY_NAV_TOP_OFFSET + 80}px 0px -55% 0px`, threshold: 0 }
    );
    sectionOrder.forEach((section) => {
      const el = refs[section];
      if (el) observer.observe(el);
    });
    return () => {
      sectionOrder.forEach((section) => {
        const el = refs[section];
        if (el) observer.unobserve(el);
      });
      observer.disconnect();
    };
  }, [sectionOrder, isSearchActive]);

  const scrollToSection = useCallback((section) => {
    const el = sectionRefs.current[section];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - STICKY_NAV_TOP_OFFSET;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveSection(section);
    }
  }, []);

  const scrollCategoriesLeft = useCallback(() => {
    if (categoriesRef.current) {
      categoriesRef.current.scrollBy({ left: (document.documentElement.dir === 'rtl' ? 200 : -200), behavior: 'smooth' });
    }
  }, []);

  const scrollCategoriesRight = useCallback(() => {
    if (categoriesRef.current) {
      categoriesRef.current.scrollBy({ left: (document.documentElement.dir === 'rtl' ? -200 : 200), behavior: 'smooth' });
    }
  }, []);

  /* Keyboard navigation for the section-chip tablist.
     ArrowLeft/Right move + activate (direction-aware so RTL feels native).
     Home/End jump to first/last. The chip's onClick handler runs because we
     call .click() on the destination — keeps focus + active-section in sync. */
  const handleChipsKeyDown = useCallback((e) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    const chips = e.currentTarget.querySelectorAll('[role="tab"]');
    if (!chips.length) return;
    const currentIdx = Array.from(chips).indexOf(document.activeElement);
    if (currentIdx === -1) return;
    e.preventDefault();
    const isRtl = document.documentElement.dir === 'rtl';
    const arrowStep = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    const step = arrowStep * (isRtl ? -1 : 1);
    let next;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = chips.length - 1;
    else next = (currentIdx + step + chips.length) % chips.length;
    chips[next].focus();
    chips[next].click();
  }, []);

  const handleLanguageToggle = useCallback(() => {
    const languages = ['en', 'es', 'ar'];
    const currentIndex = languages.indexOf(currentLang);
    const nextIndex = (currentIndex + 1) % languages.length;
    // Mark this as a deliberate diner choice so the restaurant's default_language
    // no longer overrides it on subsequent scans (see fetchData precedence).
    try { localStorage.setItem('langChosen', '1'); } catch (_) {}
    // i18n.changeLanguage triggers the languageChanged listener in i18n.js,
    // which sets <html dir> and the lang attribute and swaps the emotion
    // cache via App.js. Setting them by hand here would race the cache swap.
    i18n.changeLanguage(languages[nextIndex]);
  }, [currentLang, i18n]);

  const renderMenuItems = () => {
    return sectionOrder.map(section => {
      const categories = Object.keys(grouped[section]).sort((a, b) => {
        if (a === '_no_category') return 1;
        if (b === '_no_category') return -1;
        return a.localeCompare(b);
      });
      const sectionItemCount = Object.values(grouped[section]).reduce((sum, c) => sum + c.items.length, 0);

      return (
        <div
          key={section}
          ref={(el) => { sectionRefs.current[section] = el; }}
          data-section-id={section}
          className="menu-section"
          style={{ scrollMarginTop: 'calc(var(--menu-sticky-top) + 16px)' }}
        >
          <SectionHeader sectionName={section} displayName={sectionTranslations[section] || section} itemCount={sectionItemCount} isSticky={false} />
          {categories.map(categoryKey => {
            const { categoryName, items } = grouped[section][categoryKey];
            return (
              <div key={`${section}-${categoryKey}`} className="menu-category">
                {categoryName && <h3 className="menu-category-title">{categoryName}</h3>}
                <div className="menu-grid menu-grid--cards">
                  {items
                    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                    .map((item) => (
                      <MenuItemCard
                        key={item.id}
                        item={item}
                        currency={currency}
                        section={section}
                        getImageUrl={getImageUrl}
                        onAddToCart={addToCart}
                        onDecreaseQuantity={handleDecreaseForItem}
                        onOpenCustomize={openMenuItemModal}
                        cartQuantity={getCartQuantityForItem(item)}
                        isRTL={isRTL}
                      />
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    });
  };

  if (loading || themeLoading) {
    return <MenuLoadingSkeleton />;
  }

  const categoriesJsx = !isSearchActive && sectionOrderDisplay.length > 0 ? (
    <div className="categories-section">
      <button
        type="button"
        className="category-scroll-btn category-scroll-btn--start"
        onClick={scrollCategoriesLeft}
        aria-label={t('menu.scrollCategoriesLeft', 'Scroll categories left')}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
      </button>
      <div className="categories-container" ref={categoriesRef}>
        <Box
          className="menu-section-chips"
          role="tablist"
          aria-label={t('menu.nav.sections', 'Menu sections')}
          aria-orientation="horizontal"
          onKeyDown={handleChipsKeyDown}
        >
          {sectionOrderDisplay.map((section) => {
            const count = Object.values(grouped[section]).reduce((s, c) => s + c.items.length, 0);
            const subtitleKey = `menu.sections.${sectionToKey(section)}`;
            // Per-restaurant DB translation wins; fall back to the built-in i18n
            // section label, then the base (English) section name.
            const displayName = sectionTranslations[section]
              || (t(subtitleKey) !== subtitleKey ? t(subtitleKey) : section);
            const label = count > 0 ? `${displayName} • ${count}` : displayName;
            const isActive = activeSection === section;
            return (
              <Chip
                key={section}
                label={label}
                onClick={() => scrollToSection(section)}
                color={isActive ? 'primary' : 'default'}
                variant={isActive ? 'filled' : 'outlined'}
                className="menu-section-chip category-pill"
                component="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                sx={{
                  fontWeight: 600,
                  borderRadius: 999,
                  paddingInline: 2,
                  height: 40,
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&.MuiChip-filled': {
                    color: 'white',
                    boxShadow: '0 6px 14px rgba(0, 0, 0, 0.18)',
                  },
                  '&.MuiChip-outlined': {
                    backgroundColor: 'background.paper',
                  },
                }}
              />
            );
          })}
        </Box>
      </div>
      <button
        type="button"
        className="category-scroll-btn category-scroll-btn--end"
        onClick={scrollCategoriesRight}
        aria-label={t('menu.scrollCategoriesRight', 'Scroll categories right')}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
        </svg>
      </button>
    </div>
  ) : null;

  return (
    <main
      className={`menu-page fade-in ${isDemo ? 'menu-page--demo' : ''} ${headerShrunk ? 'menu-page--header-shrunk' : ''}`}
      data-theme={theme?.id || 'classic'}
      aria-label={t('menu.regionLabel', 'Menu')}
    >
      {isDemo && bannerCollapsed && (
        <button
          type="button"
          className="demo-banner-pill"
          onClick={() => { setBannerCollapsedPersisted(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          aria-label={t('menu.demoBanner.expand', 'Expand demo banner')}
        >
          <span className="demo-banner-pill-icon" aria-hidden="true">✨</span>
          <span className="demo-banner-pill-label">{t('menu.demoBanner.badge')}</span>
        </button>
      )}

      {isDemo && !bannerCollapsed && (
        <div className="demo-banner" role="status">
          <span className="demo-banner-badge">{t('menu.demoBanner.badge')}</span>
          <span className="demo-banner-icon" aria-hidden="true">✨</span>
          <div className="demo-banner-content">
            <span className="demo-banner-text">{t('menu.demoBanner.text')}</span>
            <ol className="demo-banner-steps">
              {[1, 2, 3, 4].map((n) => {
                const done =
                  (n === 1 && demoStepStates.browsed) ||
                  (n === 2 && demoStepStates.addedToCart) ||
                  (n === 3 && demoStepStates.ordered);
                // Step 4 ("See your dashboard") is the payoff — it's a navigation
                // action, never a checkmark. Locked until an order is placed.
                const isDashboardStep = n === 4;
                const isLocked = isDashboardStep && !demoOrderPlaced;
                const isCta = isDashboardStep && demoOrderPlaced;
                return (
                  <li
                    key={n}
                    className={`demo-banner-step ${done ? 'is-done' : ''} ${isLocked ? 'is-locked' : ''} ${isCta ? 'is-cta' : ''}`}
                  >
                    <button
                      type="button"
                      className="demo-banner-step-btn"
                      onClick={() => handleDemoStepClick(n)}
                      disabled={isLocked}
                      aria-label={isLocked ? t('menu.demoBanner.step4Locked') : t(`menu.demoBanner.step${n}`)}
                    >
                      <span className="demo-step-num" aria-hidden="true">{done ? '✓' : n}</span>
                      <span className="demo-step-label">{t(`menu.demoBanner.step${n}`)}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
            {otherDemoRestaurants.length > 0 && (
              <div className="demo-banner-switcher" aria-label={t('menu.demoBanner.switchLabel', 'Try another demo restaurant')}>
                <span className="demo-banner-switcher-label">{t('menu.demoBanner.switchLabel', 'Try another:')}</span>
                <div className="demo-banner-switcher-pills">
                  {otherDemoRestaurants.map((r) => (
                    <Link
                      key={r.id}
                      to={`/menu/${r.id}?mode=demo`}
                      className="demo-banner-switcher-pill"
                    >
                      {r.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <div className="demo-banner-actions">
              <button type="button" className="demo-banner-reset" onClick={() => { setCart([]); setShowCart(false); setDemoOrderPlaced(false); setHasScrolled(false); }} aria-label={t('menu.demoBanner.resetDemo')}>
                {t('menu.demoBanner.resetDemo')}
              </button>
              <button
                type="button"
                className="demo-banner-collapse"
                onClick={() => setBannerCollapsedPersisted(true)}
                aria-label={t('menu.demoBanner.collapse', 'Collapse demo banner')}
              >
                {t('menu.demoBanner.collapse', 'Hide')}
              </button>
            </div>
          </div>
        </div>
      )}

      {demoOrderPlaced && (
        <div className="demo-order-success demo-order-success--card" role="alert">
          <span className="demo-order-success-icon" aria-hidden="true">✓</span>
          <div className="demo-order-success-body">
            <strong>{t('menu.demoOrderSuccess.title')}</strong>
            <p>{t('menu.demoOrderSuccess.message')}</p>
            <div className="demo-order-success-actions">
              <Link
                to={`/menu/${restaurantId}/demo-dashboard`}
                state={demoOrderId ? { orderId: demoOrderId } : undefined}
                className="demo-order-success-dashboard"
              >
                {t('menu.demoOrderSuccess.viewDashboard')}
              </Link>
              <Link to="/register" className="demo-order-success-trial">
                {t('menu.demoBanner.startTrial')}
              </Link>
              <button
                type="button"
                className="demo-order-success-dismiss"
                onClick={() => setDemoOrderPlaced(false)}
              >
                {t('menu.demoOrderSuccess.keepBrowsing', 'Keep browsing')}
              </button>
            </div>
          </div>
        </div>
      )}

      <MenuHero
        heroStyle={heroStyle}
        heroBgImage={heroBgImage}
        headerShrunk={headerShrunk}
        logoUrl={logoUrl}
        restaurantName={restaurant?.name}
        restaurantId={restaurantId}
        isDemo={isDemo}
        tagline={tagline}
        tableDisplay={tableDisplay}
        isCarOrder={isCarOrder}
        phone={phone}
        currentLang={currentLang}
        onLanguageToggle={handleLanguageToggle}
      />

      {!isCarOrder && (
        <ServiceRequestButtons
          restaurantId={restaurantId}
          tableNumber={searchParams.get('table') || '1'}
        />
      )}

      {menuItems.length === 0 ? (
        loadError ? (
          <EmptyState
            icon={<span style={{ fontSize: 32 }} aria-hidden="true">⚠️</span>}
            title={t('menu.errors.loadFailedTitle', "We couldn't load the menu")}
            description={t('menu.errors.loadFailedBody', 'Check your connection and try again.')}
            action={
              <Button variant="contained" onClick={fetchData}>
                {t('menu.errors.retry', 'Try again')}
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<span style={{ fontSize: 32 }} aria-hidden="true">{isDemo ? '📋' : '🍽️'}</span>}
            title={isDemo ? t('menu.empty.demoTitle') : t('menu.empty.title')}
            description={isDemo ? t('menu.empty.demoMessage') : t('menu.empty.message')}
            action={
              !isDemo && restaurantInfo?.phone ? (
                <Button
                  variant="outlined"
                  component="a"
                  href={`tel:${restaurantInfo.phone.replace(/\s/g, '')}`}
                >
                  {t('menu.empty.callRestaurant', 'Call the restaurant')}
                </Button>
              ) : null
            }
          />
        )
      ) : (
        <>
          <Paper
            elevation={0}
            className="menu-sticky-nav"
            sx={isMobileViewport ? { position: 'static' } : { top: 'var(--menu-sticky-top)' }}
          >
            <Box className="menu-search-row">
              <MenuSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                onClear={() => setSearchDebounced('')}
                suggestions={searchSuggestions}
                popularItems={popularItems}
                placeholder={t('menu.search.placeholder')}
                isSearching={searchQuery !== searchDebounced && searchQuery.length > 0}
                onFilterClick={undefined}
                isRTL={isRTL}
              />
            </Box>
            {!isMobileViewport && categoriesJsx}
          </Paper>
          {isMobileViewport && categoriesJsx}

          {isDemo && !isSearchActive && featuredDemoItems.length > 0 && (
            <section className="demo-featured-row" aria-label={t('menu.demoFeatured.title', 'Try these')}>
              <header className="demo-featured-row-header">
                <div className="demo-featured-row-heading">
                  <h2 className="demo-featured-row-title">
                    <span aria-hidden="true">⭐</span> {t('menu.demoFeatured.title', 'Try these')}
                  </h2>
                  <p className="demo-featured-row-subtitle">{t('menu.demoFeatured.subtitle', 'Hand-picked items to get a feel for the menu')}</p>
                </div>
                {sectionOrderDisplay.length > 0 && (
                  <button
                    type="button"
                    className="demo-featured-row-viewall"
                    onClick={() => scrollToSection(sectionOrderDisplay[0])}
                  >
                    {t('menu.demoFeatured.viewAll', 'View all')}
                    <span aria-hidden="true" className="demo-featured-row-viewall-arrow">{isRTL ? '‹' : '›'}</span>
                  </button>
                )}
              </header>
              <div className="demo-featured-grid">
                {featuredDemoItems.map((item) => {
                  const imgUrl = item.image_url ? getImageUrl(item.image_url) : null;
                  return (
                    <article key={`featured-${item.id}`} className="demo-featured-card">
                      {imgUrl ? (
                        <div className="demo-featured-card-img">
                          <img src={imgUrl} alt="" loading="lazy" />
                        </div>
                      ) : (
                        <div className="demo-featured-card-img demo-featured-card-img--placeholder" aria-hidden="true">🍽️</div>
                      )}
                      <div className="demo-featured-card-body">
                        <h3 className="demo-featured-card-name">{item.name}</h3>
                        <div className="demo-featured-card-meta">
                          <span className="demo-featured-card-price">
                            <PriceTag amount={item.price} currency={currency} size="sm" color="inherit" weight="bold" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                          </span>
                          <span className="demo-featured-card-section">{item.section}</span>
                        </div>
                        <div className="demo-featured-card-actions">
                          <button
                            type="button"
                            className="demo-featured-card-add"
                            onClick={() => ((item.hasAddons || item.hasSizes) ? openMenuItemModal(item) : addToCart(item))}
                          >
                            {(item.hasAddons || item.hasSizes) ? t('menu.demoFeatured.customize', 'Customize') : t('menu.demoFeatured.add', 'Add to cart')}
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          <div className="menu-sections-container" ref={sectionsContainerRef}>
            {isSearchActive && !hasSearchResults ? (
              <EmptyState
                icon={<span style={{ fontSize: 32 }} aria-hidden="true">🔍</span>}
                title={t('menu.search.noResultsTitle', 'No matches')}
                description={t('menu.search.noResultsFor', { query: searchDebounced, defaultValue: `Nothing matched “${searchDebounced}”. Try a different word or browse the menu.` })}
                action={
                  <Button variant="outlined" onClick={() => { setSearchQuery(''); setSearchDebounced(''); }}>
                    {t('menu.search.clear')}
                  </Button>
                }
              />
            ) : (
              renderMenuItems()
            )}
          </div>
        </>
      )}

      {totalItems > 0 && (
        <button
          type="button"
          className="cart-fab floating-cart-btn"
          onClick={() => setShowCart(true)}
          aria-label={`${t('menu.viewOrder')} - ${totalItems} items, ${formatPrice(totalPrice, currency)}`}
        >
          <div className="cart-icon-wrapper">
            <span className="cart-fab-icon cart-icon" aria-hidden="true">🛒</span>
            <span className="cart-fab-badge cart-badge" aria-hidden="true">{totalItems}</span>
          </div>
          <div className="cart-info">
            <span className="cart-fab-label cart-text">{t('menu.viewOrder')}</span>
            <span className="cart-total-amount">
              <PriceTag amount={totalPrice} currency={currency} size="sm" color="inherit" weight="bold" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
            </span>
          </div>
        </button>
      )}

      {showCart && (
        <CartModal
          cart={cart}
          currency={currency}
          totalPrice={totalPrice}
          taxBreakdown={taxBreakdown}
          taxLabel={restaurant?.tax_label || 'VAT'}
          placingOrder={placingOrder}
          onClose={() => setShowCart(false)}
          onUpdateQuantity={updateQuantity}
          onRemoveItem={removeItem}
          onPlaceOrder={handlePlaceOrder}
          getItemSubtotal={getItemSubtotal}
          getImageUrl={getImageUrl}
          orderType={orderType}
          carNumber={carNumber}
          carColor={carColor}
          onCarNumberChange={setCarNumber}
          onCarColorChange={setCarColor}
        />
      )}

      <OtpCheckoutDialog
        open={otpDialogOpen}
        restaurantId={restaurantId}
        onClose={() => { if (!placingOrder) setOtpDialogOpen(false); }}
        onVerified={handleOtpVerified}
      />


      {showModal && selectedItem && (
        <MenuItemModal
          item={selectedItem}
          addons={addonsForModal}
          onClose={closeMenuItemModal}
          onAddToCart={addToCart}
          getImageUrl={getImageUrl}
          currency={currency}
        />
      )}

      <Dialog
        className="menu-order-dialog"
        open={orderResultDialogOpen}
        onClose={() => setOrderResultDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{orderResultDialogTitle}</DialogTitle>
        <DialogContent sx={{ whiteSpace: 'pre-line' }}>{orderResultDialogMessage}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderResultDialogOpen(false)} color="primary" variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
    </main>
  );
};

const Menu = () => {
  const { restaurantId } = useParams();
  const [searchParams] = useSearchParams();
  const previewThemeId = searchParams.get('theme') || null;
  return (
    <ThemeProvider restaurantId={restaurantId} previewThemeId={previewThemeId}>
      <MenuContent />
    </ThemeProvider>
  );
};

export default Menu;
